import { useEffect, useRef, useState } from 'react';
import { CHANNEL_LABEL, SENDING_NUMBERS, brokerForNumber, fmtPhone } from './convUtils';

const CONFIRM_KEY = 'conv.confirmBeforeSend';
type Ch = 'sms' | 'email';

function readConfirmPref(): boolean {
  try {
    return localStorage.getItem(CONFIRM_KEY) !== 'false'; // default true
  } catch {
    return true;
  }
}

export default function Composer({
  availableChannels,
  defaultChannel,
  recipients,
  contactName,
  dnd,
  dndChannels,
  sending,
  requestedChannel,
  sendingNumber,
  autoDraftTodo,
  onSend,
  onDraft,
}: {
  availableChannels: Ch[];
  defaultChannel: string;
  recipients: { sms?: string | null; email?: string | null };
  contactName: string;
  dnd: boolean;
  dndChannels?: { sms: boolean; email: boolean; call: boolean };
  sending: boolean;
  requestedChannel?: { ch: Ch; n: number } | null;
  sendingNumber?: string | null;
  /** TODO1: a to-do's task id - the composer asks for ONE draft for it when it opens. ⛔ The draft never sends. */
  autoDraftTodo?: string;
  onSend: (message: string, subject: string | undefined, channel: Ch, fromNumber?: string) => Promise<void>;
  onDraft: (channel: Ch, todoTaskId?: string) => Promise<string>;
}) {
  const initial: Ch = (availableChannels.includes(defaultChannel as Ch) ? (defaultChannel as Ch) : availableChannels[0]) || 'sms';
  const [channel, setChannel] = useState<Ch>(initial);
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [confirmBeforeSend, setConfirmBeforeSend] = useState(readConfirmPref);
  const [drafting, setDrafting] = useState(false);
  const [draftErr, setDraftErr] = useState<string | null>(null);
  const [sendAs, setSendAs] = useState(''); // '' = thread default; else an E.164 to force

  // Keep the selected channel valid if the contact/thread changes.
  useEffect(() => {
    if (!availableChannels.includes(channel)) setChannel(availableChannels[0] || 'sms');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableChannels.join(',')]);

  // Panel "Text"/"Email" buttons request a channel switch.
  useEffect(() => {
    if (requestedChannel && availableChannels.includes(requestedChannel.ch)) setChannel(requestedChannel.ch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedChannel?.n]);

  const doDraft = async (todoTaskId?: string) => {
    setDrafting(true);
    setDraftErr(null);
    try {
      const text = await onDraft(channel, todoTaskId);
      // ⛔ The to-do's draft arrives on its own, seconds later: it never replaces words he typed meanwhile.
      if (text) setMessage((typed) => (todoTaskId && typed.trim() ? typed : text));
      else setDraftErr('No draft returned');
    } catch (e) {
      // A to-do's draft says the server's words - one that is not his is "To-do not found".
      if (todoTaskId && e instanceof Error && e.message) { setDraftErr(e.message); return; }
      setDraftErr('Couldn’t draft - write it yourself');
    } finally {
      setDrafting(false);
    }
  };

  // TODO1: a to-do's "Draft message" - ONE draft when the composer opens: not on every render, and not again after a
  // send (the composer stays mounted for its thread). ⛔ It only fills the box; sending is still his tap.
  const autoDrafted = useRef(false);
  useEffect(() => {
    if (!autoDraftTodo || autoDrafted.current || availableChannels.length === 0) return;
    autoDrafted.current = true;
    void doDraft(autoDraftTodo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoDraftTodo, availableChannels.length]);

  if (availableChannels.length === 0) {
    return (
      <div className="border-t border-gray-200 px-4 py-3 text-center text-xs text-gray-400">
        This contact has no phone or email on file to reply to. Manage it in GoHighLevel.
      </div>
    );
  }

  const isEmail = channel === 'email';
  const recipient = (isEmail ? recipients.email : recipients.sms) || 'this contact';

  const toggleConfirm = () => {
    const next = !confirmBeforeSend;
    setConfirmBeforeSend(next);
    try { localStorage.setItem(CONFIRM_KEY, String(next)); } catch { /* ignore */ }
  };

  const channelBlocked = !!dndChannels?.[channel];

  const submit = async () => {
    const body = message.trim();
    if (!body || sending) return;
    if (confirmBeforeSend || dnd || channelBlocked) {
      const warn = channelBlocked
        ? `⚠️ ${contactName} has ${CHANNEL_LABEL[channel]} set to Do-Not-Disturb.\n\n`
        : dnd
        ? `⚠️ ${contactName} is marked Do-Not-Disturb.\n\n`
        : '';
      const ok = window.confirm(`${warn}Send ${CHANNEL_LABEL[channel]} to ${recipient}?`);
      if (!ok) return;
    }
    try {
      const fromNumber = !isEmail && sendAs ? sendAs : undefined;
      await onSend(body, isEmail ? subject.trim() || undefined : undefined, channel, fromNumber);
      setMessage('');
      setSubject('');
    } catch {
      // The thread's failed bubble says why (CONV2); the draft stays in the box, so Send tries again.
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const tab = (active: boolean) =>
    `flex items-center gap-1 text-xs px-3 py-1 rounded-full transition-colors ${
      active ? 'bg-primary-500 text-white' : 'border border-gray-300 text-gray-600 hover:border-gray-400'
    }`;

  return (
    <div className="border-t border-gray-200 px-3 py-2.5">
      <div className="flex items-center gap-2 mb-2">
        {availableChannels.map((ch) => (
          <button key={ch} type="button" onClick={() => setChannel(ch)} className={tab(channel === ch)}>
            {CHANNEL_LABEL[ch]}
          </button>
        ))}
        <button
          type="button"
          onClick={() => void doDraft()}
          disabled={drafting}
          className="ml-auto flex items-center gap-1 text-xs px-3 py-1 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          title="Draft a reply with AI from the thread + persona (you edit before sending)"
        >
          {drafting ? 'Drafting…' : '✦ AI draft'}
        </button>
      </div>

      {/* SMS sending-line display + "Send as" override */}
      {channel === 'sms' && (sendingNumber || SENDING_NUMBERS.length > 0) && (
        <div className="flex items-center gap-2 mb-1.5 text-[11px] text-gray-500">
          {(() => {
            const eff = sendAs || sendingNumber || '';
            const who = brokerForNumber(eff);
            return <span>Sending from {eff ? fmtPhone(eff) : 'the thread line'}{who ? ` (${who})` : ''}</span>;
          })()}
          {SENDING_NUMBERS.length > 0 && (
            <select
              value={sendAs}
              onChange={(e) => setSendAs(e.target.value)}
              className="ml-auto text-[11px] border border-gray-300 rounded px-1.5 py-0.5 bg-white"
              title="Choose which Apex line this sends from"
            >
              <option value="">Thread default</option>
              {SENDING_NUMBERS.map((n) => (
                <option key={n.number} value={n.number}>Send as {n.broker}</option>
              ))}
            </select>
          )}
        </div>
      )}

      {draftErr && <div className="mb-1.5 text-[11px] text-warning">{draftErr}</div>}
      {channelBlocked ? (
        <div className="mb-1.5 text-[11px] text-red-600">⚠️ {CHANNEL_LABEL[channel]} is on Do-Not-Disturb for {contactName}. Sends require confirmation.</div>
      ) : dnd ? (
        <div className="mb-1.5 text-[11px] text-warning">⚠️ {contactName} is marked Do-Not-Disturb. Sends require confirmation.</div>
      ) : null}

      {isEmail && (
        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject"
          className="w-full mb-2 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
        />
      )}
      <div className="flex items-end gap-2">
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={onKeyDown}
          rows={2}
          placeholder={`Reply by ${CHANNEL_LABEL[channel]} to ${recipient}…`}
          className="flex-1 resize-none px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
        />
        <button
          type="button"
          onClick={submit}
          disabled={sending || !message.trim()}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50"
        >
          {sending ? 'Sending…' : `Send ${CHANNEL_LABEL[channel]}`}
        </button>
      </div>
      <label className="mt-1.5 flex items-center gap-1.5 text-[11px] text-gray-400 select-none">
        <input type="checkbox" checked={confirmBeforeSend} onChange={toggleConfirm} />
        Confirm before sending
      </label>
    </div>
  );
}
